import { DateTime, Duration } from 'luxon'
import { Snapshot } from './Snapshot'
import { TimeStep } from './TimeStep'
import { City } from './City'
import { ISnapshotLike } from './ISnapshotLike'
import { TimeStepMax } from './TimeStepMax'

export class FilteredSnapshot<T extends TimeStep> implements ISnapshotLike<T>, Iterable<T> {
  private alignmentInterval: number | null = null

  constructor(private readonly base: Snapshot<T>, private readonly predicate: (ts: T) => boolean) {}

  setAlignmentInterval(minutes: number): void {
    this.alignmentInterval = minutes
  }

  private roundToInterval(timestamp: DateTime, intervalMinutes: number): DateTime {
    const totalMinutes = timestamp.hour * 60 + timestamp.minute
    const roundedMinutes = intervalMinutes * Math.floor(totalMinutes / intervalMinutes)
    return timestamp.set({
      hour: Math.floor(roundedMinutes / 60),
      minute: roundedMinutes % 60,
      second: 0,
      millisecond: 0,
    })
  }

  getStart(): DateTime {
    // Calculate start based on rounded reference timestamp for symmetry
    if (this.alignmentInterval) {
      const roundedRef = this.roundToInterval(this.base.timestamp, this.alignmentInterval)
      return roundedRef.minus({ hours: 6 })
    }
    return this.base.getStart()
  }

  getEnd(): DateTime {
    // Calculate end based on rounded reference timestamp for symmetry
    if (this.alignmentInterval) {
      const roundedRef = this.roundToInterval(this.base.timestamp, this.alignmentInterval)
      return roundedRef.plus({ hours: 6 })
    }
    return this.base.getEnd()
  }
  getDurationBefore(): Duration {
    return this.base.getDurationBefore()
  }
  getDurationAfter(): Duration {
    return this.base.getDurationAfter()
  }

  get city(): City {
    return this.base.city
  }

  get timestamp(): DateTime {
    if (this.alignmentInterval) {
      return this.roundToInterval(this.base.timestamp, this.alignmentInterval)
    }
    return this.base.timestamp
  }

  getHistory(): T[] {
    return this.base.getHistory().filter(this.predicate)
  }

  getForecasts(): T[] {
    return this.base.getForecasts().filter(this.predicate)
  }

  getReference(): T {
    const baseRef = this.base.getReference()
    // If base reference passes the filter, return it
    if (this.predicate(baseRef)) {
      return baseRef
    }
    // Otherwise, find the closest aligned timestep
    const history = this.getHistory()
    if (history.length > 0) {
      return history[history.length - 1] // Last history item (most recent aligned)
    }
    const forecasts = this.getForecasts()
    if (forecasts.length > 0) {
      return forecasts[0] // First forecast item
    }
    // Fallback to base reference if no filtered timesteps exist
    return baseRef
  }

  getMax(): TimeStepMax | null {
    const max = this.base.getMax()
    return max
  }

  isEmpty(): boolean {
    return !this.toArray().some((ts) => !ts.isEmpty())
  }

  findTimeStepAt(timestamp: DateTime): T | null {
    const lookup = timestamp.toUTC().toSeconds()
    const timeSteps = this.toArray()
    const candidate = timeSteps.find((timeStep: T) => timeStep.timestamp.toUTC().toSeconds() === lookup)

    return candidate ?? null
  }

  nextTimeStep(timestamp: DateTime | null): T | null {
    if (timestamp == null) return null

    const lookup = timestamp.toUTC().toSeconds()
    const timeSteps = this.toArray()
    const candidate = timeSteps.find((timeStep: T, index) =>
      index > 0
        ? timeSteps[index - 1].timestamp.toUTC().toSeconds() === lookup
        : timeSteps[timeSteps.length - 1].timestamp.toUTC().toSeconds() === lookup,
    )

    return candidate ?? null
  }

  private toArray(): Array<T> {
    const timeSteps = [...this.getHistory()]
    const reference = this.getReference()
    // Only include reference if it passes the filter
    if (reference && this.predicate(reference)) {
      timeSteps.push(reference)
    }
    timeSteps.push(...this.getForecasts())

    return timeSteps
  }

  *[Symbol.iterator](): Iterator<T> {
    yield* this.toArray()
  }
}
